import { FunctionalComponent } from 'preact';
import { stylesheet } from 'astroturf';
import { useEffect, useRef } from 'preact/hooks';
import { Nav } from '@accedo/spatial-nav';
import { memo } from 'preact/compat';
import {
  LiveChannel,
  LiveShowForOverlay,
} from '##/services/tenApi/collections/types';
import { focusCache } from '##/services/store/store';
import { handleVideoClick } from '##/utils/TileClickHandlers';
import TealiumBridge from '##/services/analytics/Tealium/TealiumBridge';
import { LIVE_ROW_ID_PREFIX } from '##/utils';
import AdjustedLiveCarouselTile from '../LiveCarousel/AdjustedLiveCarouselTile';
import NextLiveCarouselTile from '../LiveCarousel/NextLiveCarouselTile';
import { LiveCarouselItem } from '../LiveCarousel/types';

const HEIGHT_OF_LIVE_ROW = 116;

const getScrollOffset = (
  index: number,
  firstChannelGroupHeight: number,
): number => {
  switch (index) {
    case 0:
      return firstChannelGroupHeight;
    case 1:
      return HEIGHT_OF_LIVE_ROW + firstChannelGroupHeight;
    default:
      return HEIGHT_OF_LIVE_ROW * 2;
  }
};

const scaleOffset = (value = 0): number =>
  window.innerHeight <= 720 ? value : (value / 720) * 1080;

const styles = stylesheet`
  @import '##/styles/import.scss';

  .live-page-container {
      width: 100vw;
      height: 100vh;
      padding-left: pxToVw(83);
      font-family: barlow;
     
  };

  .live-headers {
    margin-top: pxToVw(63);
    margin-bottom: pxToVw(21);
    display: flex;
    flex-direction: row;
    p {
      font-size: pxToVw(22);
    }
    .live-schedule-header {
      margin-left: pxToVw(34);      
    }
  }

  .full-carousel{
    overflow: hidden;
  }

  .live-content {
    display:flex;
    flex-direction:row;
  }

`;

// const livePagelazyLoadBoundary = 4;
interface LiveTVRowType {
  liveItem: LiveChannel;
  index: number;
  currentTime: number;
  setHoverFocus: (hoverFocus: number | null) => void;
  hoverFocus: number | string | null;
  setLiveShow: (liveShow: LiveShowForOverlay) => void;
  setShowLiveOverlay: (show: boolean) => void;
  firstGroupHeadingHeight: number;
}
const BaseLiveTvRow: FunctionalComponent<LiveTVRowType> = ({
  liveItem,
  index,
  currentTime,
  setHoverFocus,
  hoverFocus,
  setLiveShow,
  setShowLiveOverlay,
  firstGroupHeadingHeight,
}) => {
  const liveItemUpdates = useRef(liveItem);

  useEffect(() => {
    liveItemUpdates.current = liveItem;
  }, [liveItem]);

  const beforeClickHandler = (
    channelId: string,
    isComingUp: boolean,
    Later: boolean,
  ): void => {
    setHoverFocus(null);
    focusCache.livePage.$set({
      lastFocusChannelId: channelId,
      lastChannelIdx: liveItem?.channelIdx,
      isComingUp,
      Later,
    });
  };

  const onSelectHandler = (
    num: number,
    liveItem: LiveChannel,
    navId: string,
  ): void => {
    if (!liveItem || !liveItem?.schedule) {
      return;
    }

    const timeInMin = Math.floor(
      Math.min(liveItem?.schedule?.[num]?.startTime - Date.now() / 1000) / 60,
    );

    const liveItemWithTealiumFields = {
      ...liveItem,
      carouselTitle: 'Live TV',
      carouselType: 'list',
      carouselNumberOfItems: liveItem?.schedule?.length,
      carouselItemIndex: num,
      carouselVerticalIndex: liveItem?.channelIdx,
      carouselInteractionCategory: liveItem?.genre,
    } as LiveCarouselItem;

    focusCache.livePage.$set({
      lastFocusedElementId: navId,
    });

    if (timeInMin <= 0) {
      beforeClickHandler(liveItem.key as string, false, false);
      handleVideoClick(liveItemWithTealiumFields);
    } else {
      beforeClickHandler(liveItem.key as string, num === 1, num === 2);

      TealiumBridge.onCarouselItemClick(liveItemWithTealiumFields);

      const liveScheduleItem = liveItem.schedule[num] || {};

      setLiveShow({
        ...liveScheduleItem,
        channelLogoUrl: liveItem.logoUrl,
      });
      setShowLiveOverlay(true);
    }
  };

  const rowId = `${LIVE_ROW_ID_PREFIX}-${index}`;
  return (
    <Nav.Horizontal id={rowId} index={index}>
      <div
        className={styles.liveContent}
        id={rowId}
        data-scroll-offset={
          -scaleOffset(getScrollOffset(index, firstGroupHeadingHeight))
        }
      >
        {liveItem?.schedule?.map((elem, num) => {
          if (num === 0) {
            const liveNowId = `live-now-tile-${index}`;
            return (
              <Nav.Focusable
                onFocus={() => {
                  setHoverFocus(index);
                }}
                id={`live-now-tile-${index}`}
                onSelect={() =>
                  onSelectHandler(num, liveItemUpdates.current, liveNowId)
                }
              >
                <AdjustedLiveCarouselTile
                  {...liveItem}
                  liveProgress={currentTime || 0}
                  focused={hoverFocus === index}
                  broughtToYouByLogo={liveItem?.sponsorLogoUrl}
                />
              </Nav.Focusable>
            );
          }
          if (num < 3) {
            const text = num === 1 ? 'next' : 'later';
            const id = `${text}-tile-${index}`;

            return (
              <Nav.Focusable
                id={id}
                onFocus={() => {
                  setHoverFocus(index);
                }}
                onSelect={() =>
                  onSelectHandler(num, liveItemUpdates.current, id)
                }
              >
                <NextLiveCarouselTile
                  {...elem}
                  color={liveItem.color}
                  focused={hoverFocus === index}
                  label={text}
                />
              </Nav.Focusable>
            );
          }
          return null;
        })}
      </div>
    </Nav.Horizontal>
  );
};

const isHoverFocusAndIndexIdentical = (propsData: LiveTVRowType): boolean =>
  propsData.hoverFocus === propsData.liveItem.channelIdx;

const LiveTvRow = memo(
  BaseLiveTvRow,
  (prevProps, nextProps) =>
    !(
      isHoverFocusAndIndexIdentical(nextProps) ||
      isHoverFocusAndIndexIdentical(prevProps) ||
      prevProps.currentTime !== nextProps.currentTime ||
      prevProps.liveItem.liveShow?.startTime !==
        nextProps.liveItem.liveShow?.startTime
    ),
);

export { LiveTvRow };
